/* global T */
import { getPois as apiGetPois } from "@/api/allMap/csMap.js";

const EARTH_RADIUS = 6378137;

function ensureT() {
    if(typeof window === 'undefined' || !window.T) {
        throw new Error('天地图 SDK 未加载');
    }
}

export function toLngLat(point) {
    if(!point) return null;
    if(point.lng !== undefined && point.lat !== undefined) {
        return new T.LngLat(point.lng, point.lat);
    }
    if(Array.isArray(point) && point.length === 2) {
        return new T.LngLat(point[0], point[1]);
    }
    return point;
}

function normalizeAddressComponent(raw = {}) {
    return {
        province: raw.province || '',
        city: raw.city || raw.province || '',
        district: raw.county || '',
        street: raw.town || '',
        streetNumber: raw.poi || ''
    };
}

function rad(val) {
    return (val * Math.PI) / 180;
}

export function getPoint(lng, lat) {
    ensureT();
    return new T.LngLat(lng, lat);
}

export function getBounds(points) {
    // 支持坐标数组格式 [[lng, lat], ...]
    const lngs = points.map(point => point[0]); // 提取所有经度
    const lats = points.map(point => point[1]); // 提取所有纬度

    const minLng = Math.min(...lngs);
    const maxLng = Math.max(...lngs);
    const minLat = Math.min(...lats);
    const maxLat = Math.max(...lats);
    return [minLng, minLat, maxLng, maxLat];
}

// 判断点是否在多个环组成区域内
export function isPointInPolygon(point, polygon) {
    if(!Array.isArray(point) || !Array.isArray(polygon) || polygon.length < 3) {
        return false;
    }
    const [x, y] = point;
    let inside = false;
    for(let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
        const [xi, yi] = polygon[i];
        const [xj, yj] = polygon[j];
        const intersect = ((yi > y) !== (yj > y))
            && (x < ((xj - xi) * (y - yi)) / ((yj - yi) || Number.EPSILON) + xi);
        if(intersect) inside = !inside;
    }
    return inside;
}

// 判断环是否在另一个环内
export function isRingInRing(ring, polygon) {
    if(!Array.isArray(ring) || ring.length === 0) return false;
    return ring.every(point => isPointInPolygon(point, polygon));
}


// 获取多边形中心点
export function getPolygonCenter(points) {
    var sortedLongitudeArray = points.map(item => item[0]).sort();// 首先对经度进行排序，红色部分是array中经度的名称
    var sortedLatitudeArray = points.map(item => item[1]).sort();// 对纬度进行排序，红色部分是array中纬度的名称
    var centerLongitude = ((parseFloat(sortedLongitudeArray[0]) + parseFloat(sortedLongitudeArray[sortedLongitudeArray.length - 1])) / 2).toFixed(6);
    const centerLatitude = ((parseFloat(sortedLatitudeArray[0]) + parseFloat(sortedLatitudeArray[sortedLatitudeArray.length - 1])) / 2).toFixed(6);
    return [centerLongitude, centerLatitude];
}

// 获取多边形右下角坐标
export function getPolygonBottomRight(points) {
    var sortedLongitudeArray = points.map(item => item[0]).sort();
    var sortedLatitudeArray = points.map(item => item[1]).sort();
    var bottomRightLongitude = parseFloat(sortedLongitudeArray[sortedLongitudeArray.length - 1]).toFixed(6);
    const bottomRightLatitude = parseFloat(sortedLatitudeArray[0]).toFixed(6);
    return [bottomRightLongitude, bottomRightLatitude];
}

/**
 * 将坐标转换为地址信息
 * @param {Object} point - 坐标对象，包含lng和lat属性
 * @returns {Promise} - 返回Promise，resolve地址信息，reject错误信息
 */
export function getAddress(point, option = { extensions: "all" }) {
    return new Promise((resolve, reject) => {
        void option;
        if(!point || typeof point.lng !== 'number' || typeof point.lat !== 'number') {
            reject(new Error('无效的坐标参数'));
            return;
        }
        try {
            ensureT();
            const geocoder = new T.Geocoder();
            geocoder.getLocation(toLngLat(point), (result) => {
                const component = normalizeAddressComponent(result?.getAddressComponent?.().addressComponent || result || {});
                resolve({
                    addressComponent: component,
                    pois: component.poi ? [{ address: component.poi }] : [],
                    formattedAddress: [
                        component.province,
                        component.city,
                        component.district,
                        component.street,
                        component.streetNumber
                    ].join('')
                });
            });
        } catch (error) {
            reject(error);
        }
    });
}

/**
 * 将地址转换为坐标信息
 * @param {string} address - 地址字符串
 * @returns {Promise} - 返回Promise，resolve坐标信息，reject错误信息
 */
export function getLocation(address, option = { extensions: "all" }) {
    return new Promise((resolve, reject) => {
        void option;
        if(!address || typeof address !== 'string') {
            reject(new Error('无效的地址参数'));
            return;
        }
        try {
            ensureT();
            const geocoder = new T.Geocoder();
            geocoder.getPoint(address, ({ location: lnglat }) => {
                if(lnglat && lnglat.lon !== undefined && lnglat.lat !== undefined) {
                    resolve([{ location: { lng: lnglat.lon, lat: lnglat.lat }}]);
                }else {
                    reject(new Error('地址解析失败，无法设置标记点'));
                }
            });
        } catch (error) {
            reject(error);
        }
    });
}

/**
 * 关键词搜索地址
 * @param {string} keyword - 搜索关键词
 * @param {object} option - 搜索选项
 * @returns {Promise} - 返回Promise，resolve搜索结果，reject错误信息
*/
export function getPois(address, option = {
    citylimit: true,
    pageSize: 10,
    page: 1
}) {
    if(!address || typeof address !== 'string') {
        return Promise.reject(new Error('无效的地址参数'));
    }
    const params = Object.assign({ name: address }, option);
    return apiGetPois(params).then(res => {
        if(res.success && res.data) {
            if(res.data.status == 0) {
                return Promise.reject(new Error(res.data.info || '地址解析失败，无法设置标记点'));
            }
            const pois = res.data.pois || [];
            return pois.map(item => {
                item.location = {
                    lat: item.lat,
                    lng: item.lng
                };
                return item;
            });
        }
        return Promise.reject(new Error('地址解析失败，无法设置标记点'));
    });
}

/**
 * 关键词搜索地址
 * @param {string} keyword - 搜索关键词
 * @returns {Promise} - 返回Promise，resolve搜索结果，reject错误信息
*/
export function searchPlace(keyword, option = {}) {
    if(!keyword || typeof keyword !== 'string') {
        return Promise.reject(new Error('无效的关键词参数'));
    }
    const params = Object.assign({ name: keyword }, option);
    return apiGetPois(params).then(res => {
        if(res.success && res.data) {
            const pois = res.data.pois || [];
            return {
                count: pois.length,
                pois: pois.map(item => {
                    item.location = {
                        lat: item.lat,
                        lng: item.lng
                    };
                    return item;
                })
            };
        }
        return Promise.reject(new Error('关键词搜索失败，无法获取地址信息'));
    });
}

export async function searchAllPlace(keyword, option = {}) {
    if(!keyword || typeof keyword !== 'string') {
        return Promise.reject(new Error('无效的关键词参数'));
    }
    const all = [];
    let currentPage = 1;
    let total = 0;
    let hasMore = true;

    do {
        try {
            const params = Object.assign({ name: keyword, page: currentPage }, option);
            const res = await apiGetPois(params);
            if(res.success && res.data) {
                const list = res.data.pois || [];
                const pois = list.map(item => {
                    item.location = {
                        lat: item.lat,
                        lng: item.lng
                    };
                    return item;
                });
                if(total === 0) {
                    total = res.data.count || pois.length;
                }
                if(pois.length === 0) {
                    hasMore = false;
                }else {
                    all.push(...pois);
                    if(all.length >= total) {
                        hasMore = false;
                    }else {
                        currentPage++;
                    }
                }
            }else {
                hasMore = false;
            }
        } catch (error) {
            console.log(error);
            break;
        }
    } while(hasMore);

    return all;
}

/**
 * 计算多边形面积
 * @param {Array} paths - 多边形路径点数组
 * @returns {number} - 返回多边形面积（单位：平方米）
 */
export function calculateArea(paths) {
    if(!Array.isArray(paths) || paths.length < 3) {
        return 0;
    }
    let area = 0;
    for(let i = 0; i < paths.length; i++) {
        const [lng1, lat1] = paths[i];
        const [lng2, lat2] = paths[(i + 1) % paths.length];
        area += rad(lng2 - lng1) * (2 + Math.sin(rad(lat1)) + Math.sin(rad(lat2)));
    }
    return Math.abs((area * EARTH_RADIUS * EARTH_RADIUS) / 2);
}

/**
 * 创建一个点模块
 * @param {Object} point - 坐标对象，包含lng和lat属性
 * @param {string} id - 点模块的唯一标识符
 * @returns {Object} - 返回创建的点模块对象
 */
export function createDot(point, id) {
    const obj = {
        id: id,
        name: `中心点${id}`,
        ploatId: 'ploat' + id,
        point: point, // 中心点经纬度对象（兼容百度地图）
        lnglat: point ? [point.lng, point.lat] : [], // 中心点经纬度数组（兼容高德地图）
        addressComponent: {
            province: '',
            city: '',
            district: '',
            street: '',
            streetNumber: ''
        },
        radius: 1000,
        address: '',
        markers: [] // 标记点下的社区
    };
    return obj;
}

export function createRound(point, id) {
    const obj = {
        id: id,
        name: `中心点${id}`,
        aroundId: "around" + id,
        point: point, // 中心点经纬度对象（兼容百度地图）
        lnglat: point ? [point.lng, point.lat] : [], // 中心点经纬度数组（兼容高德地图）
        addressComponent: {
            province: '',
            city: '',
            district: '',
            street: '',
            streetNumber: ''
        },
        radius: 1000,
        address: '',
        markers: [] // 标记点下的社区
    };
    return obj;
}

export function createPolygon(points, id) {
    const obj = {
        id: id,
        name: `范围${id}`,
        ploatId: 'ploat' + id,
        point: null, // 中心点经纬度对象（兼容百度地图）
        lnglat: null, // 中心点经纬度数组（兼容高德地图）
        addressComponent: {
            province: '',
            city: '',
            district: '',
            street: '',
            streetNumber: ''
        },
        address: '',
        paths: points, // 多边形路径点
        markers: []
    };
    return obj;
}

export function useCount() {
    let count = 0;

    const add = () => {
        count++;
    };

    // 使用 getter 获取最新的 count 值
    return {
        get count() {
            return count;
        },
        add
    };
}
